<template>
  <el-dialog
    :title="dialogTitle"
    :visible="visible"
    width="660px"
    append-to-body
    :close-on-click-modal="false"
    @close="$emit('update:visible', false)"
  >
    <!-- 详情与草稿同步完成前整块锁定，避免异步回填覆盖用户已填的上报内容 -->
    <div
      v-loading="detailLoading"
      class="fault-dialog-body"
    >
      <!-- 故障基本信息 -->
      <div class="fault-info-card">
        <div class="fault-info-row">
          <span class="label">故障编号：</span>
          <span>{{ displayFault.faultCode }}</span>
          <span class="label ml">设备名称：</span>
          <span>{{ displayFault.deviceName }}</span>
          <el-tag v-if="displayFault.deviceSource === 'external'" size="mini">非系统设备</el-tag>
          <el-tag
            v-if="displayFault.deviceLevel === 'A'"
            type="danger"
            size="mini"
            style="margin-left:6px"
          >
            重要设备
          </el-tag>
        </div>
        <div class="fault-info-row fault-info-row--block">
          <span class="label">场所名称：</span>
          <span class="desc">{{ displayFault.areaPath || '-' }}</span>
        </div>
        <div class="fault-info-row fault-info-row--block">
          <span class="label">具体位置：</span>
          <span class="desc">{{ displayFault.installLocation || '-' }}</span>
        </div>
      </div>

      <!-- 待处理单：处置方式、子表单 -->
      <template v-if="fault.status === 'pending'">
        <el-form
          label-width="110px"
          style="margin-bottom:8px"
        >
          <el-form-item label="处置方式">
            <el-radio-group v-model="handleType">
              <el-radio label="self">
                自行处理
              </el-radio>
              <el-radio label="report">
                上报甲方
              </el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>

        <!-- 故障处置完结信息 -->
        <FaultHandleFix
          v-if="handleType === 'self'"
          ref="selfHandle"
          variant="self"
        />

        <!-- 故障上报处理信息 -->
        <FaultHandleReport
          v-if="handleType === 'report'"
          ref="reportPanel"
          status="pending"
          :report-form="reportForm"
          :fault="fault"
          :display-fault="displayFault"
          :fault-photo-thumb-urls="faultPhotoThumbUrls"
          :fault-photos-loading="faultPhotosLoading"
          :fault-photo-ids-count="faultPhotoIdsFromDetail.length"
          :has-fault-photo-ids="hasFaultPhotoIds"
          :report-extra-photo-thumb-urls="reportExtraPhotoThumbUrls"
          :report-extra-photos-loading="reportExtraPhotosLoading"
          :report-extra-photo-ids-count="reportExtraPhotoIdsFromDetail.length"
          :has-report-extra-photo-ids="hasReportExtraPhotoIds"
        />
      </template>

      <!-- 已上报单：上报信息只读 + 维修登记 -->
      <template v-else-if="fault.status === 'reported'">
        <FaultHandleReport
          status="reported"
          :report-form="reportForm"
          :fault="fault"
          :display-fault="displayFault"
          :fault-photo-thumb-urls="faultPhotoThumbUrls"
          :fault-photos-loading="faultPhotosLoading"
          :fault-photo-ids-count="faultPhotoIdsFromDetail.length"
          :has-fault-photo-ids="hasFaultPhotoIds"
          :report-extra-photo-thumb-urls="reportExtraPhotoThumbUrls"
          :report-extra-photos-loading="reportExtraPhotosLoading"
          :report-extra-photo-ids-count="reportExtraPhotoIdsFromDetail.length"
          :has-report-extra-photo-ids="hasReportExtraPhotoIds"
          :downloading="downloading"
          @download-report="handleDownloadReportedFile"
        />

        <el-divider content-position="left">
          维修情况登记
        </el-divider>
        <!-- 故障维修处理信息 -->
        <FaultHandleFix
          ref="repairHandle"
          variant="repair"
        />
      </template>
    </div>

    <!-- 按钮组 -->
    <div
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        size="small"
        @click="$emit('update:visible', false)"
      >
        取消
      </el-button>

      <!-- 待处理单：上报甲方 -->
      <template v-if="fault.status === 'pending' && handleType === 'report'">
        <el-button
          type="warning"
          plain
          size="small"
          :loading="savingDraft"
          @click="handleSaveDraft"
        >
          保存草稿
        </el-button>
        <el-button
          type="primary"
          plain
          size="small"
          :loading="previewing"
          @click="handleSaveAndPreview"
        >
          保存并预览
        </el-button>
        <el-button
          type="primary"
          size="small"
          :loading="submitting"
          @click="handleDownloadAndReport"
        >
          下载上报文件
        </el-button>
      </template>

      <!-- 待处理单：自行处理 -->
      <el-button
        v-else-if="fault.status === 'pending' && handleType !== 'report'"
        type="primary"
        size="small"
        :loading="submitting"
        @click="handleSubmit"
      >
        提交处置
      </el-button>

      <!-- 已上报单：已完成维修 -->
      <el-button
        v-else-if="fault.status === 'reported'"
        type="primary"
        size="small"
        :loading="submitting"
        @click="handleFinishRepair"
      >
        已完成维修
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
// 服务
import axios from 'axios';
import { faultService } from './fault.service';
// 工具函数
import { parseImageFileIds } from '@/features/components/common-image/imageFileIdsUtil.js';
import { downloadFromApiBlobResponse } from '@/features/common/utils/downloadFileUtil.js';
import {
  buildInitialReportFormFromFault,
  defaultHandleTypeForPending,
  loadFaultPhotoThumbRecords,
  loadReportExtraThumbRecordsFromReportContent,
  mergeReportFormFromDetail,
  parseReportExtraPhotoIdsFromDetail,
  serializeReportContent
} from '@/features/gnmk/fault/utils/faultHandleUtils.js';
// 组件
import FaultHandleFix from './faultHandleFix.vue';
import FaultHandleReport from './faultHandleReport.vue';

export default {
  name: 'FaultHandleDialog',
  components: { FaultHandleFix, FaultHandleReport },
  props: {
    visible: { type: Boolean, default: false },
    fault: { type: Object, required: true }
  },
  data () {
    return {
      handleType: defaultHandleTypeForPending(this.fault),
      reportForm: buildInitialReportFormFromFault(this.fault),
      submitting: false,
      savingDraft: false,
      previewing: false,
      downloading: false,
      faultDetail: null,
      detailLoading: false,
      /** 现场缩略图逐张请求中：仅方框内转圈 */
      faultPhotosLoading: false,
      /** 补充现场缩略图请求中 */
      reportExtraPhotosLoading: false,
      faultPhotoUrls: [],
      /** 已上报状态只读：补充现场图缩略图 */
      reportExtraPhotoUrls: []
    };
  },
  computed: {
    displayFault () {
      return this.faultDetail || this.fault;
    },
    dialogTitle () {
      if (this.fault.status === 'reported') return '故障处置 - 维修登记';
      return '故障处置';
    },
    faultPhotoThumbUrls () {
      return this.faultPhotoUrls.map(p => p.url).filter(Boolean);
    },
    reportExtraPhotoThumbUrls () {
      return this.reportExtraPhotoUrls.map(p => p.url).filter(Boolean);
    },
    /** 是否有故障现场图 fileId（用于缩略图行展示与加载中占位，不依赖已解码的 url） */
    faultPhotoIdsFromDetail () {
      return parseImageFileIds(this.displayFault && this.displayFault.faultImages);
    },
    hasFaultPhotoIds () {
      return this.faultPhotoIdsFromDetail.length > 0;
    },
    /** 已上报单：补充现场图 fileId，来自详情 reportContent */
    reportExtraPhotoIdsFromDetail () {
      return parseReportExtraPhotoIdsFromDetail(this.faultDetail);
    },
    hasReportExtraPhotoIds () {
      return this.reportExtraPhotoIdsFromDetail.length > 0;
    }
  },
  methods: {
    async loadFaultDetail () {
      if (!this.fault || !this.fault.uuid) return;
      this.detailLoading = true;
      this.faultPhotosLoading = false;
      this.reportExtraPhotosLoading = false;
      this.faultDetail = null;
      this.faultPhotoUrls = [];
      this.reportExtraPhotoUrls = [];
      try {
        const res = await faultService.getFaultDetail(this.fault.uuid);
        this.faultDetail = res.data;
        this.applyReportFormFromDetail();
      } finally {
        this.detailLoading = false;
      }
      if (!this.faultDetail) return;

      // 获取对应图片
      const token = this.$HMCookies.get(this.$config.serverHeaderTokenName);
      const headers = { [this.$config.serverHeaderTokenName]: token };
      const base = `${this.$config.ctx.iffApi}/common/image/`;

      const needFaultThumbs = parseImageFileIds(this.faultDetail.faultImages).length > 0;
      const needExtraThumbs = this.reportExtraPhotoIdsFromDetail.length > 0;
      if (needFaultThumbs) {
        this.faultPhotosLoading = true;
        try {
          this.faultPhotoUrls = await loadFaultPhotoThumbRecords(this.faultDetail.faultImages, axios, headers, base);
        } finally {
          this.faultPhotosLoading = false;
        }
      }
      if (needExtraThumbs) {
        this.reportExtraPhotosLoading = true;
        try {
          this.reportExtraPhotoUrls = await loadReportExtraThumbRecordsFromReportContent(
            this.faultDetail.reportContent,
            axios,
            headers,
            base
          );
        } finally {
          this.reportExtraPhotosLoading = false;
        }
      }
    },

    /**
     * 详情加载后与草稿合并：上报补充表单以服务端详情为准（含最新 reportContent）
     */
    applyReportFormFromDetail () {
      const merged = mergeReportFormFromDetail(this.faultDetail);
      if (!merged) return;
      this.reportForm = merged.next;
      if (merged.shouldSetHandleTypeReport) {
        this.handleType = 'report';
      }
    },

    /**
     * 保存草稿
     */
    handleSaveDraft () {
      this.savingDraft = true;
      faultService.saveReportDraft({
        uuid: this.fault.uuid,
        reportContent: serializeReportContent(this.reportForm)
      }).then(() => {
        this.$message.success('草稿保存成功');
      }).finally(() => {
        this.savingDraft = false;
      });
    },

    /**
     * 先落库草稿（避免只预览不落库），再拉预览流下载
     */
    handleSaveAndPreview () {
      this.$refs.reportPanel.validateReportForm().then(() => {
        this.previewing = true;
        const draftPayload = {
          uuid: this.fault.uuid,
          reportContent: serializeReportContent(this.reportForm)
        };
        return faultService
          .saveReportDraft(draftPayload)
          .then(() => faultService.previewReportFile({ uuid: this.fault.uuid }));
      }).then(res => {
        downloadFromApiBlobResponse(res, '故障上报单（预览）.docx');
        this.$message.success('草稿已保存，预览文件已下载');
      }).finally(() => {
        this.previewing = false;
      });
    },

    /**
     * 下载上报文件-状态流转
     */
    handleDownloadAndReport () {
      this.$refs.reportPanel.validateReportForm().then(() => {
        return this.$confirm(
          '文件下载后，上报信息将不可修改，故障单状态将变为已上报待处理，是否确认下载？',
          '确认上报',
          { confirmButtonText: '确认下载', cancelButtonText: '再想想', type: 'warning' }
        );
      }).then(() => {
        this.submitting = true;
        return faultService.reportAndDownload({
          uuid: this.fault.uuid,
          reportContent: serializeReportContent(this.reportForm)
        });
      }).then(res => {
        downloadFromApiBlobResponse(res, '故障上报单.docx');
        this.$message.success('上报成功，状态已更新为"已上报待处理"');
        this.$emit('success');
      }).finally(() => {
        this.submitting = false;
      });
    },

    /**
     * 提交处置-自行处理
     */
    handleSubmit () {
      this.$refs.selfHandle.validateAndGetPayload().then((payload) =>
        this.$confirm('是否确认已完成维修？', '确认提交', {
          confirmButtonText: '确认',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => payload)
      ).then((payload) => {
        this.submitting = true;
        return faultService.handleFault({
          uuid: this.fault.uuid,
          handleType: 'self',
          handleDesc: payload.handleDesc,
          handleImages: payload.handleImages.length
            ? payload.handleImages.join(',')
            : null
        });
      }).then(() => {
        this.$message.success('维修处置成功');
        this.$emit('success');
      }).finally(() => {
        this.submitting = false;
      });
    },

    /**
     * 完成维修-已上报单
     */
    handleFinishRepair () {
      this.$refs.repairHandle.validateAndGetPayload().then((payload) =>
        this.$confirm('是否确认已完成维修？', '确认提交', {
          confirmButtonText: '确认',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => payload)
      ).then((payload) => {
        this.submitting = true;
        return faultService.finishRepair({
          uuid: this.fault.uuid,
          handleDesc: payload.handleDesc,
          handleImages: payload.handleImages.length
            ? payload.handleImages.join(',')
            : null
        });
      }).then(() => {
        this.$message.success('维修结案成功');
        this.$emit('success');
      }).finally(() => {
        this.submitting = false;
      });
    },

    /**
     * 下载已上报文件
     */
    handleDownloadReportedFile () {
      this.downloading = true;
      faultService.downloadReportedFile(this.fault.uuid).then(res => {
        downloadFromApiBlobResponse(res, '故障上报单.docx');
      }).finally(() => {
        this.downloading = false;
      });
    }
  },
  watch: {
    // immediate 保证首次展示即拉取详情与故障照片
    visible: {
      handler (val) {
        if (val && this.fault && this.fault.uuid) {
          this.loadFaultDetail();
        } else if (!val) {
          this.faultDetail = null;
          this.faultPhotoUrls = [];
          this.reportExtraPhotoUrls = [];
          this.faultPhotosLoading = false;
          this.reportExtraPhotosLoading = false;
        }
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss" scoped>
.fault-dialog-body {
  min-height: 120px;
}

.fault-info-card {
  background: #f5f7fa;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
}

.fault-info-row {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  &:last-child { margin-bottom: 0; }
  &--block {
    align-items: flex-start;
    flex-wrap: wrap;
    .label { padding-top: 2px; }
  }
  &--photos {
    margin-bottom: 0;
  }
}

.label { color: #909399; flex-shrink: 0; }
.ml { margin-left: 24px; }
.desc { color: #303133; flex: 1; }

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
